import { PlatformRole, UserStatus } from '@repo/shared'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  useIsMobile,
} from '@repo/ui'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Plus, Search } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { SortState } from '@/components/shared/sortable-table-head'
import type { AdminUsersListParams } from '@repo/api'

import { ListPagination } from '@/components/shared/list-pagination'
import { toggleSort } from '@/components/shared/sortable-table-head'
import { UserCard } from '@/features/admin/users/components/user-card'
import { UsersTable } from '@/features/admin/users/components/users-table'
import { useAdminUsersPaginated } from '@/features/admin/users/hooks/use-admin-users'

export const Route = createFileRoute('/admin/users/')({
  component: AdminUsersListPage,
})

const ALL = 'all'

type SortKey = NonNullable<AdminUsersListParams['sort']>

const SORT_KEYS: Array<SortKey> = [
  'firstName',
  'email',
  'organization',
  'platformRole',
  'status',
  'createdAt',
]

// Table column `name` maps to backend column `firstName`.
const toSortKey = (key: string | undefined): SortKey => {
  if (!key) return 'firstName'
  if (key === 'name') return 'firstName'
  return (SORT_KEYS as Array<string>).includes(key)
    ? (key as SortKey)
    : 'firstName'
}

function AdminUsersListPage() {
  const { t } = useTranslation(['admin', 'common'])
  const isMobile = useIsMobile()

  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, { wait: 300 })
  const [platformRoleFilter, setPlatformRoleFilter] = useState<string>(ALL)
  const [statusFilter, setStatusFilter] = useState<string>(ALL)
  const [sort, setSort] = useState<SortState>({
    key: 'name',
    direction: 'asc',
  })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const { data, isPending, isFetching } = useAdminUsersPaginated({
    page,
    pageSize,
    search: debouncedSearch.trim() || undefined,
    platformRole:
      platformRoleFilter !== ALL
        ? (platformRoleFilter as PlatformRole)
        : undefined,
    status: statusFilter !== ALL ? (statusFilter as UserStatus) : undefined,
    sort: toSortKey(sort?.key),
    sortDir: sort?.direction ?? 'asc',
  })

  const handleSort = (key: string) => {
    setSort((prev) => toggleSort(prev, key))
    setPage(1)
  }

  const items = data?.items ?? []
  const totalCount = data?.totalCount ?? 0
  const hasFilters =
    debouncedSearch.trim().length > 0 ||
    platformRoleFilter !== ALL ||
    statusFilter !== ALL

  return (
    <div className="space-y-4">
      <Card className="bg-background">
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>{t('users.list.title')}</CardTitle>
              <CardDescription>
                {totalCount} {t('users.list.totalCount')}
              </CardDescription>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:flex md:items-center">
              <div className="relative col-span-full md:w-64">
                <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                <Input
                  placeholder={t('users.list.searchPlaceholder')}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    setPage(1)
                  }}
                  className="h-11 pl-9"
                />
              </div>

              <Select
                value={platformRoleFilter}
                onValueChange={(v) => {
                  setPlatformRoleFilter(v)
                  setPage(1)
                }}
              >
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>
                    {t('users.list.allRoles')}
                  </SelectItem>
                  {Object.values(PlatformRole).map((r) => (
                    <SelectItem key={r} value={r}>
                      {t(`users.platformRoles.${r}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={statusFilter}
                onValueChange={(v) => {
                  setStatusFilter(v)
                  setPage(1)
                }}
              >
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>
                    {t('users.list.allStatuses')}
                  </SelectItem>
                  {Object.values(UserStatus).map((s) => (
                    <SelectItem key={s} value={s}>
                      {t(`users.statuses.${s}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button asChild size="lg" className="primary-bg-gradient">
                <Link to="/admin/users/create">
                  <Plus className="size-4" />
                  {t('users.list.create')}
                </Link>
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {isPending ? (
            isMobile ? (
              <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-24 w-full rounded-md" />
                ))}
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border">
                <Skeleton className="h-10 w-full rounded-none" />
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton
                    key={i}
                    className="h-14 w-full rounded-none border-t"
                  />
                ))}
              </div>
            )
          ) : items.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground text-sm">
                {hasFilters ? t('users.list.noMatches') : t('users.list.empty')}
              </p>
            </div>
          ) : (
            <div
              className={
                isFetching ? 'opacity-60 transition-opacity' : undefined
              }
            >
              {isMobile ? (
                <div className="space-y-2">
                  {items.map((u) => (
                    <UserCard key={u.id} user={u} />
                  ))}
                </div>
              ) : (
                <UsersTable users={items} sort={sort} onSort={handleSort} />
              )}

              <ListPagination
                page={data!.page}
                totalPages={data!.totalPages}
                pageSize={data!.pageSize}
                totalCount={data!.totalCount}
                hasPrevious={data!.hasPrevious}
                hasNext={data!.hasNext}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size)
                  setPage(1)
                }}
                className="mt-4"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
